import React, { useRef, useState } from "react";
import { creditDaysOnFile } from "../../utils/paymentTerms";

/**
 * The payment-terms flag on the review panel, and the one-click answer to it
 * (client meeting 2026-09-25, Lawrence Marketing: vendor on file at 30 days,
 * invoice read as CASH).
 *
 * The flag sentence is the SERVER's (`utils/paymentTermsCheck`, kept fresh by
 * `vendorInvoicePaymentTerms.service`); nothing here re-derives it. The button
 * only writes the invoice's own Payment Terms — the same field, and through the
 * same save, as typing into the Payment Terms cell — so the due date, the
 * re-check and everything else that follows a terms edit run exactly as for a
 * manual edit, and the flag goes because the server's check now agrees. The
 * vendor record is never touched.
 *
 * The on-file figure is read the way the server's check reads it
 * (`vendorCreditDaysFromReconciliation` + `creditDaysOnFile`): a confirmed
 * vendor's credit-days snapshot, which that service keeps in step with the
 * Vendor record in the same write as the flag. No confirmed vendor, or no
 * terms on file, means there is nothing to adopt, so no button.
 */
export default function PaymentTermsFlag(props: {
  flag: string | null;
  vendorReconciliation?: { matchedVendorId?: string | null; bestCandidate?: any } | null;
  /** Mirrors the Payment Terms cell's guard: view mode cannot edit. */
  readOnly: boolean;
  /** Why the button is held right now (a save in flight, typed terms unsaved); null when free. */
  blockedReason?: string | null;
  /** Saves the invoice's Payment Terms. Must surface its own errors; not expected to reject. */
  onUseVendorTerms: (terms: string) => Promise<void>;
}) {
  const { flag, vendorReconciliation, readOnly, blockedReason = null, onUseVendorTerms } = props;
  const [saving, setSaving] = useState(false);
  // State reaches `disabled` only on the next render; the ref closes the
  // double-click window synchronously (the USC-BUG-0045 lesson).
  const savingRef = useRef(false);
  if (!flag) return null;

  const onFileDays = vendorReconciliation?.matchedVendorId
    ? creditDaysOnFile(vendorReconciliation?.bestCandidate?.creditDays)
    : null;
  // Written as a day count paymentTerms.parsePaymentTerms reads back as the
  // same number, so the server's check sees agreement.
  const terms = onFileDays === null ? null : `${onFileDays} day${onFileDays === 1 ? "" : "s"}`;

  async function apply() {
    if (!terms || readOnly || blockedReason || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      await onUseVendorTerms(terms);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="space-y-1">
      <div className="text-xs font-semibold text-amber-700">Payment terms</div>
      <div className="text-xs text-amber-800 bg-amber-100 rounded-md px-3 py-1.5 flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1">{flag}</span>
        {terms && (
          <button
            type="button"
            onClick={() => void apply()}
            disabled={readOnly || saving || !!blockedReason}
            title={readOnly ? "Open the invoice in Edit mode to change its payment terms." : blockedReason ?? undefined}
            className="shrink-0 rounded-md border border-amber-400 bg-white px-2 py-0.5 font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {`Use vendor's terms (${terms})`}
          </button>
        )}
      </div>
    </div>
  );
}
